import { inject } from 'vue'
import { useTypesense } from '@/composables/useTypesense'
import { useDeviceStore } from '@/stores/device.js'
import { fetchEdgeFunctionError } from '@/utils/supabase'

const RECOMMENDATION_COUNT = 3

// Ordered by preference: optical is what Wings is mostly here to sell, but a customer who has
// only ever bought sunglasses gets sun picks rather than an empty block. NBP ranks separately
// per use case, so only one is shown at a time and its divergence line matches it.
const RECOMMENDATION_USE_CASES = ['optical', 'sun']

export function useNbp() {
  const supabase = inject('supabase')
  const typesense = useTypesense()
  const deviceStore = useDeviceStore()

  async function getProductsForSkus(skuIds) {
    if (!skuIds.length) return new Map()

    const skus = [...new Set(skuIds.map((skuId) => skuId.toUpperCase()))]

    const searchResults = await typesense
      .client()
      .collections(import.meta.env.VITE_TYPESENSE_PRODUCTS_COLLECTION)
      .documents()
      .search({
        q: '*',
        filter_by: `sku:=[${skus.join(',')}] && availability.availableMarkets := ${deviceStore.settings.country.toLowerCase()} && availability.stores := true`,
        sort_by: `_text_match:desc,stock.${deviceStore.settings.warehouse}.status:asc,priority:asc`,
        limit: skus.length,
        include_fields: `sku,image,price,size,title,type,modelId,tags,colour,finish,collection,collaboration,doNotTrack,productStatus,stock.${deviceStore.settings.warehouse}`
      })

    return new Map(searchResults.hits.map((hit) => [hit.document.sku, hit.document]))
  }

  const EMPTY_RESULT = { recommendations: [], divergence: null, useCase: null }

  /**
   * Looks up NBP (next-best-product) recommendations for a customer email.
   * Picks come from a single use case, preferring optical over sun - see
   * RECOMMENDATION_USE_CASES.
   */
  async function getProductRecommendations(email) {
    if (!email) return EMPTY_RESULT

    const { data, error } = await supabase.functions.invoke('nbp', {
      body: { action: 'lookup', email },
      region: 'eu-central-1'
    })

    const errorMessage = await fetchEdgeFunctionError(error)
    if (errorMessage || !data) return EMPTY_RESULT

    const top3 = data.top3 ?? []
    const useCase = RECOMMENDATION_USE_CASES.find((candidate) =>
      top3.some((pick) => pick.use_case === candidate)
    )

    if (!useCase) return EMPTY_RESULT

    const picks = top3
      .filter((pick) => pick.use_case === useCase)
      .sort((a, b) => a.rec_rank - b.rec_rank)
      .slice(0, RECOMMENDATION_COUNT)

    const divergence = (data.divergence ?? []).find((d) => d.use_case === useCase) ?? null

    const productsBySku = await getProductsForSkus(picks.map((pick) => pick.sku_id))
    const recommendations = picks
      .map((pick) => {
        const product = productsBySku.get(pick.sku_id.toUpperCase())
        if (!product) return null

        return pick.rec_rank === 1
          ? { ...product, tags: [...(product.tags ?? []), 'Top Pick'] }
          : product
      })
      .filter(Boolean)

    return { recommendations, divergence, useCase }
  }

  return { getProductRecommendations }
}
